import React, { useState, useEffect } from "react";
import {
  formatWhatsApp,
  validateWhatsApp,
  validateEmail,
  validateName,
} from "../utils/masks";
import { trackFormStep, trackFormSubmission } from "../utils/analytics";

const BRAZILIAN_STATES = [
  "MT",
  "MS",
  "GO",
  "SP",
  "MG",
  "PR",
  "SC",
  "RS",
  "RJ",
  "ES",
  "BA",
  "SE",
  "AL",
  "PE",
  "PB",
  "RN",
  "CE",
  "PI",
  "MA",
  "TO",
  "PA",
  "AP",
  "AM",
  "RR",
  "AC",
  "RO",
  "DF",
];

interface LeadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  userType: string;
  state: string;
  objective: string;
  name: string;
  whatsapp: string;
  email: string;
  newsletter: boolean;
}

interface FormErrors {
  [key: string]: string;
}

const LeadModal: React.FC<LeadModalProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  // Funções para mapear valores em inglês para português
  const getUserTypeLabel = (userType: string) => {
    const labels: { [key: string]: string } = {
      producer: "Produtor Rural",
      company: "Representante de Empresa",
      distributor: "Distribuidor/Revendedor",
      technician: "Técnico Agrícola",
      other: "Outro",
    };
    return labels[userType] || userType;
  };

  const getObjectiveLabel = (objective: string) => {
    const labels: { [key: string]: string } = {
      buy: "Comprar produtos agrícolas",
      sell: "Vender produtos/serviços",
      learn: "Conhecer oportunidades de negócio",
      other: "Outro objetivo",
    };
    return labels[objective] || objective;
  };

  const [formData, setFormData] = useState<FormData>({
    userType: "",
    state: "",
    objective: "",
    name: "",
    whatsapp: "",
    email: "",
    newsletter: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleInputChange = (field: keyof FormData, value: any) => {
    if (field === "whatsapp") {
      value = formatWhatsApp(value);
    }

    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateStep = (step: number) => {
    const newErrors: FormErrors = {};

    switch (step) {
      case 1:
        if (!formData.userType) {
          newErrors.userType = "Selecione o tipo de usuário";
        }
        break;
      case 2:
        if (!formData.state) {
          newErrors.state = "Selecione o estado";
        }
        break;
      case 3:
        if (!formData.objective) {
          newErrors.objective = "Selecione o objetivo principal";
        }
        break;
      case 4:
        if (!validateName(formData.name)) {
          newErrors.name = "Nome deve ter pelo menos 2 caracteres";
        }

        const hasWhatsApp =
          formData.whatsapp && validateWhatsApp(formData.whatsapp);
        const hasEmail = formData.email && validateEmail(formData.email);

        if (!hasWhatsApp && !hasEmail) {
          newErrors.contact = "Preencha pelo menos WhatsApp ou Email";
        } else {
          // Se WhatsApp foi preenchido, valida
          if (formData.whatsapp && !validateWhatsApp(formData.whatsapp)) {
            newErrors.whatsapp = "WhatsApp deve ter 11 dígitos";
          }
          // Se email foi preenchido, valida
          if (formData.email && !validateEmail(formData.email)) {
            newErrors.email = "Email inválido";
          }
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      trackFormStep(currentStep, {
        [getCurrentStepField()]: getCurrentStepValue(),
      });

      // Se estiver no step 4, enviar os dados imediatamente
      if (currentStep === 4) {
        submitForm();
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const getCurrentStepField = () => {
    const fields = ["userType", "state", "objective", "contact"];
    return fields[currentStep - 1];
  };

  const getCurrentStepValue = () => {
    switch (currentStep) {
      case 1:
        return formData.userType;
      case 2:
        return formData.state;
      case 3:
        return formData.objective;
      case 4:
        return `${formData.name} - ${formData.whatsapp}`;
      default:
        return "";
    }
  };

  const submitForm = async () => {
    if (!validateStep(4)) return;

    setIsLoading(true);

    try {
      const response = await fetch(
        "https://api.appagronegocios.com.br/api/leads",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            user_type: formData.userType,
            state: formData.state,
            objective: formData.objective,
            name: formData.name,
            whatsapp: formData.whatsapp.replace(/\D/g, ""),
            email: formData.email,
            newsletter: true,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`);
      }

      const result = await response.json();
      console.log("Lead cadastrado com sucesso:", result);

      trackFormSubmission(formData);
      setShowSuccess(true);

      // Reset form after 3 seconds and redirect based on user type
      setTimeout(() => {
        setShowSuccess(false);
        setCurrentStep(1);
        setFormData({
          userType: "",
          state: "",
          objective: "",
          name: "",
          whatsapp: "",
          email: "",
          newsletter: false,
        });
        onClose();

        // Redirect based on user type
        if (
          formData.userType === "company" ||
          formData.userType === "distributor"
        ) {
          // Redirect to WhatsApp with personalized message
          const message = encodeURIComponent(
            `Olá! Conheci o App AgroNegócios através do site e gostaria de saber mais.\n\n` +
              `Sou ${getUserTypeLabel(
                formData.userType
              ).toLowerCase()} do estado de ${
                formData.state
              } e tenho interesse em ${getObjectiveLabel(
                formData.objective
              ).toLowerCase()}.\n\n` +
              `Meu nome é ${formData.name}. Poderia me contar mais sobre as oportunidades de negócio da plataforma?`
          );
          const whatsappUrl = `https://wa.me/5566999842002?text=${message}`;
          window.open(whatsappUrl, "_blank");
        } else {
          // Redirect to Instagram (try app first, fallback to web)
          const instagramWeb = `https://www.instagram.com/app_agro_negocios`;

          // Check if we're on mobile to try Instagram app
          const isMobile =
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent
            );

          if (isMobile) {
            // Try to open Instagram app first
            const instagramApp = `instagram://user?username=app_agro_negocios`;
            window.location.href = instagramApp;

            // Fallback to web after delay
            setTimeout(() => {
              window.open(instagramWeb, "_blank");
            }, 2000);
          } else {
            // Desktop - open web version directly
            window.open(instagramWeb, "_blank");
          }
        }
      }, 3000);
    } catch (error) {
      console.error("Erro ao enviar formulário:", error);
      alert("Erro ao enviar formulário. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const getProgressPercentage = () => {
    return (currentStep / 4) * 100;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl">
          {/* Header */}
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-agro-primary">
                Acesso Antecipado
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs font-medium text-gray-600 mb-2">
                <span>Etapa {currentStep} de 4</span>
                <span>{Math.round(getProgressPercentage())}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-agro-primary rounded-full h-2 transition-all duration-300"
                  style={{ width: `${getProgressPercentage()}%` }}
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {showSuccess ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-2xl font-bold text-agro-primary mb-2">
                  Obrigado!
                </h3>
                <p className="text-gray-600 mb-4">
                  Você está na lista de acesso antecipado!
                </p>
                <p className="text-sm text-gray-500">
                  {formData.userType === "company" ||
                  formData.userType === "distributor"
                    ? "Redirecionando para WhatsApp..."
                    : "Redirecionando para Instagram..."}
                </p>
              </div>
            ) : (
              <>
                {/* Step 1: User Type */}
                {currentStep === 1 && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      Qual é o seu perfil?
                    </h4>
                    <div className="space-y-3">
                      {[
                        { value: "producer", label: "🌱 Produtor Rural" },
                        {
                          value: "company",
                          label: "🏢 Representante de Empresa",
                        },
                        {
                          value: "distributor",
                          label: "🚛 Distribuidor/Revendedor",
                        },
                        { value: "technician", label: "👨‍🔬 Técnico Agrícola" },
                        { value: "other", label: "👤 Outro" },
                      ].map((option) => (
                        <label
                          key={option.value}
                          className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            formData.userType === option.value
                              ? "border-agro-primary bg-agro-light"
                              : "border-gray-200 hover:border-agro-secondary"
                          }`}
                        >
                          <input
                            type="radio"
                            name="userType"
                            value={option.value}
                            checked={formData.userType === option.value}
                            onChange={(e) =>
                              handleInputChange("userType", e.target.value)
                            }
                            className="sr-only"
                          />
                          <span className="text-lg">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {errors.userType && (
                      <p className="text-red-500 text-sm">{errors.userType}</p>
                    )}
                  </div>
                )}

                {/* Step 2: State */}
                {currentStep === 2 && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      Em qual estado você atua?
                    </h4>
                    <select
                      value={formData.state}
                      onChange={(e) =>
                        handleInputChange("state", e.target.value)
                      }
                      className={`w-full p-4 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-agro-primary/50 ${
                        errors.state ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      <option value="">Selecione o estado</option>
                      {BRAZILIAN_STATES.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                    {errors.state && (
                      <p className="text-red-500 text-sm">{errors.state}</p>
                    )}
                  </div>
                )}

                {/* Step 3: Objective */}
                {currentStep === 3 && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      Qual é o seu principal objetivo?
                    </h4>
                    <div className="space-y-3">
                      {[
                        {
                          value: "buy",
                          label: "🛒 Comprar produtos agrícolas",
                        },
                        { value: "sell", label: "💰 Vender produtos/serviços" },
                        {
                          value: "learn",
                          label: "📚 Conhecer oportunidades de negócio",
                        },
                        { value: "other", label: "🎯 Outro objetivo" },
                      ].map((option) => (
                        <label
                          key={option.value}
                          className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            formData.objective === option.value
                              ? "border-agro-primary bg-agro-light"
                              : "border-gray-200 hover:border-agro-secondary"
                          }`}
                        >
                          <input
                            type="radio"
                            name="objective"
                            value={option.value}
                            checked={formData.objective === option.value}
                            onChange={(e) =>
                              handleInputChange("objective", e.target.value)
                            }
                            className="sr-only"
                          />
                          <span className="text-lg">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {errors.objective && (
                      <p className="text-red-500 text-sm">{errors.objective}</p>
                    )}
                  </div>
                )}

                {/* Step 4: Contact Info */}
                {currentStep === 4 && (
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      Dados para contato
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Preencha pelo menos WhatsApp ou Email para contato
                    </p>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome completo *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        className={`w-full p-4 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-agro-primary/50 ${
                          errors.name ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="Seu nome completo"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        WhatsApp
                      </label>
                      <input
                        type="tel"
                        value={formData.whatsapp}
                        onChange={(e) =>
                          handleInputChange("whatsapp", e.target.value)
                        }
                        className={`w-full p-4 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-agro-primary/50 ${
                          errors.whatsapp ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="(11) 99999-9999"
                      />
                      {errors.whatsapp && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.whatsapp}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        className={`w-full p-4 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-agro-primary/50 ${
                          errors.email ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="seu@email.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    {/* Mensagem de erro geral para contato */}
                    {errors.contact && (
                      <p className="text-red-500 text-sm font-medium">
                        {errors.contact}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-3 mt-8">
                  {currentStep > 1 && (
                    <button
                      onClick={prevStep}
                      className="flex-1 btn-secondary"
                      disabled={isLoading}
                    >
                      Voltar
                    </button>
                  )}

                  {currentStep < 4 ? (
                    <button onClick={nextStep} className="flex-1 btn-primary">
                      Continuar
                    </button>
                  ) : currentStep === 4 ? (
                    <button
                      onClick={nextStep}
                      disabled={isLoading}
                      className="flex-1 btn-primary"
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Finalizando...
                        </div>
                      ) : (
                        "Finalizar Cadastro"
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={submitForm}
                      disabled={isLoading}
                      className="flex-1 btn-primary"
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Finalizando...
                        </div>
                      ) : (
                        "Finalizar Cadastro"
                      )}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadModal;
